import React, { FC, useMemo } from 'react';
import { ImageRenderer } from 'src/common/ImageRenderer';
import { VisitorView } from './constants';
import linkedInLogo from 'src/logos/linkedin.svg';
import twitterLogo from 'src/logos/twitter.svg';
import facebookLogo from 'src/logos/facebook.svg';
import { DesktopStrongIcon, MobileStrongIcon, TabletStrongIcon, UnknownStrongIcon } from 'src/monet/icons';
import { TooltipComponent } from 'src/common/tooltip/component';
import { DeviceType } from 'src/dashboard/insights/helpers';

export function getDeviceTypeTooltip(deviceType?: DeviceType): string {
  if (!deviceType) {
    return 'Unknown Device';
  }
  const { form, os } = deviceType;
  let osStr = '';
  if (os === 'android') {
    osStr = 'Android';
  } else if (os === 'ios') {
    osStr = 'iOS';
  } else if (os === 'macos') {
    osStr = 'Mac OS';
  } else if (os === 'windows') {
    osStr = 'Windows';
  }
  let formStr = 'Unknown Device';
  if (form === 'mobile') {
    formStr = 'Mobile';
  } else if (form === 'desktop') {
    formStr = 'Desktop';
  } else if (form === 'tablet') {
    formStr = 'Tablet';
  }

  if (os) {
    return `${osStr}, ${formStr}`;
  }
  if (form) {
    return formStr;
  }
  return `Unknown Device`;
}

export function getDeviceTypeIcon(deviceType?: DeviceType): JSX.Element {
  if (deviceType?.form === 'mobile') {
    return <MobileStrongIcon />;
  }
  if (deviceType?.form === 'desktop') {
    return <DesktopStrongIcon />;
  }
  if (deviceType?.form === 'tablet') {
    return <TabletStrongIcon />;
  }
  return <UnknownStrongIcon />;
}

type EditorInsightsVisitorCardProps = {
  visitorView: VisitorView;
};

export const EditorInsightsVisitorCard: FC<EditorInsightsVisitorCardProps> = ({ visitorView }) => {
  const occupationCompanyInfo = useMemo(() => {
    if (visitorView.occupation_title && visitorView.company?.name) {
      return `${visitorView.occupation_title}, ${visitorView.company?.name}`;
    }
    if (visitorView.occupation_title) {
      return visitorView.occupation_title;
    }
    if (visitorView.company?.name) {
      return visitorView.company?.name;
    }
    return undefined;
  }, [visitorView]);

  const locationTimeInfo = useMemo(() => {
    if (visitorView.location) {
      return `${visitorView.location}, ${visitorView.created_at}`;
    }
    return visitorView.created_at;
  }, [visitorView]);

  return (
    <div className='flex flex-col'>
      <div className='mb-2 text-bedrock-p-strong text-bedrock-black'>{visitorView.name || 'Anonymous'}</div>
      {occupationCompanyInfo && <div className='text-bedrock-p text-bedrock-black'>{occupationCompanyInfo}</div>}
      {visitorView.email && (
        <div className='text-bedrock-p text-bedrock-black underline'>
          <a
            onClick={(e) => e && e.stopPropagation()}
            href={`mailto:${visitorView.email}`}
            target='_blank'
            rel='noopener noreferrer'
          >
            {visitorView.email}
          </a>
        </div>
      )}
      {locationTimeInfo && <div className='mt-1 text-bedrock-p-small text-bedrock-black'>{locationTimeInfo}</div>}
      <div className='flex items-center pt-6'>
        {visitorView.company?.logo && (
          <div>
            {visitorView.company?.domain ? (
              <a
                href={`https://${visitorView.company?.domain}`}
                target='_blank'
                rel='noopener noreferrer'
                onClick={(e) => e && e.stopPropagation()}
              >
                <ImageRenderer className='rounded-lg h-4 mr-6' src={visitorView.company?.logo} />
              </a>
            ) : (
              <ImageRenderer className='rounded-lg h-4 mr-6' src={visitorView.company?.logo} />
            )}
          </div>
        )}
        {(visitorView.social_media || []).length > 0 && (
          <div className='flex gap-1 mr-6'>
            {(visitorView.social_media || []).map(({ handle, url, type }) => (
              <a
                key={`${type}-profile-${handle}`}
                onClick={(e) => e && e.stopPropagation()}
                href={url}
                target='_blank'
                rel='noopener noreferrer'
              >
                <ImageRenderer
                  alt={type}
                  className='w-4 h-4'
                  src={
                    type === 'facebook'
                      ? facebookLogo
                      : type === 'linkedin'
                      ? linkedInLogo
                      : type === 'twitter'
                      ? twitterLogo
                      : null
                  }
                />
              </a>
            ))}
          </div>
        )}
        <div>
          <TooltipComponent placement='top' tooltipContent={getDeviceTypeTooltip(visitorView.device_type)}>
            {visitorView.device_type?.form === 'mobile' ? (
              <MobileStrongIcon />
            ) : visitorView.device_type?.form === 'desktop' ? (
              <DesktopStrongIcon />
            ) : visitorView.device_type?.form === 'tablet' ? (
              <TabletStrongIcon />
            ) : (
              <UnknownStrongIcon />
            )}
          </TooltipComponent>
        </div>
      </div>
    </div>
  );
};
